import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Button } from 'react-bootstrap';
import { isEqual, isEmpty } from 'lodash';
import { Image, Loader } from '@components/common';
import Actions, { thunks } from '@redux/app';
import { BankAccountVerificationModal, ConfirmationModal } from '@components/modals';
import utils, { constants } from '@utils';
import { AddPaymentMethodModal } from '../../../../components';

const SETTINGS_TYPE = 'payment';

class PaymentMethods extends PureComponent {
	constructor(props) {
		super(props);

		this.state = {
			paymentMethods : [],
		};
	}

	componentDidMount() {
		this.fetchPaymentMethods();
	}

	UNSAFE_componentWillReceiveProps = ({ data, isRkPay }) => {
		const lastProps = this.props;

		if ((lastProps.isRkPay !== isRkPay) && isRkPay) {
			this.fetchPaymentMethods(true);
		} else if (!isEmpty(data) && !isEqual(lastProps.data, data)) {
			this.setState({ paymentMethods : utils.setPaymentMethods(data) });
		}
	}

	componentWillUnmount = () => {
		const { resetForm } = this.props;
		resetForm(SETTINGS_TYPE);
		resetForm('addPayment');
	}

	fetchPaymentMethods = (force = false) => {
		const { isRkPay, getPaymentMethods } = this.props;
		if (force || isRkPay) getPaymentMethods();
	}

	showAddPaymentModal = () => {
		const { setField } = this.props;

		setField('addPayment', 'showAddPayment', true);
	}

	deletePaymentMethod = (id) => () => {
		const { deletePaymentMethod } = this.props;

		this._confirmationModal.openModal({
			title     : 'Confirm',
			subTitle  : 'Are you sure you want to remove this payment method?',
			onConfirm : () => deletePaymentMethod(id),
		});
	}

	showVerifyBankAccountModal = (id) => {
		const { setField } = this.props;
		setField('bankAccountVerification', 'bankAccountId', id);
		setField('bankAccountVerification', 'show', true);
	}

	render() {
		const {
			loading,
			setRef,
			user,
			setPreferredCard,
		} = this.props;

		const { paymentMethods } = this.state;

		return (
			<div
				className="card-section settings-payment-methods"
				ref={(ref) => setRef(ref)}
			>
				<div className="row">
					<div className="col-xs-8">
						<h5 className="color-dark font-bold margin-bottom-20 flex-1">Payment Methods</h5>
					</div>
					<div className="col-xs-4 text-right">
						<If condition={paymentMethods.length}>
							<Button
								bsStyle="link"
								onClick={this.showAddPaymentModal}
							>
								+ Add
							</Button>
						</If>
					</div>
				</div>
				<Choose>
					<When condition={paymentMethods.length}>
						<For each="account" of={paymentMethods} index="index">
							<div key={`account-${account.id}`} className="row margin-bottom-15">
								<Choose>
									<When condition={account.type === 'bank_account'}>
										<div className="col-sm-6">
											{ `${account.bankName} ending in ${account.accNumber}` }
										</div>
									</When>
									<When condition={account.type === 'card'}>
										<div className="col-xs-6 col-sm-3">
											<Image
												className="pull-left"
												src={constants.CARD_ICONS[account.cardType] || constants.CARD_ICONS.default}
											/>
											{account.cardNumber}
										</div>
										<div className="col-xs-6 col-sm-3 card-expiry text-right">
											<small>
												{ `Exp: ${account.expiry}` }
											</small>
											<If condition={account.isDefault}>
												<span className="settings-label label-success">Default</span>
											</If>
										</div>
									</When>
								</Choose>
								<div className="col-xs-6 col-sm-3">
									<Button
										bsStyle="link"
										className="color-pink-brink"
										onClick={this.deletePaymentMethod(account.id)}
									>
										Remove
									</Button>
								</div>
								<div className="col-xs-6 col-sm-3 text-right">
									<Choose>
										<When condition={account.type === 'bank_account' && account.accStatus === constants.BANK_ACCOUNT_STATUSES.NEW.value}>
											<Button
												bsStyle="link"
												onClick={() => this.showVerifyBankAccountModal(account.id)}
											>
												Verify
											</Button>
										</When>
										<When condition={!account.isDefault}>
											<Button
												bsStyle="link"
												onClick={() => setPreferredCard(account.id)}
											>
												Set as preferred
											</Button>
										</When>
									</Choose>
								</div>
							</div>
						</For>
					</When>
					<Otherwise>
						<div className="flex-center flex-direction-column">
							<p>Please add a payment method to complete your signup</p>
							<Button
								bsStyle="primary"
								onClick={this.showAddPaymentModal}
							>
								+ Add Payment Method
							</Button>
						</div>
					</Otherwise>
				</Choose>
				<AddPaymentMethodModal user={user} callback={this.fetchPaymentMethods} />
				<ConfirmationModal ref={(ref) => { this._confirmationModal = ref; }} />
				<BankAccountVerificationModal />
				<Loader active={loading} />
			</div>
		);
	}
}

const mapStateToProps = ({
	app : {
		user,
		user : { IsRKPay : isRkPay },
		payment : {
			loading,
			data,
		},
	},
}) => ({
	user,
	isRkPay,
	loading,
	data,
});

const mapDispatchToProps = (dispatch) => ({
	setField            : bindActionCreators(Actions, dispatch).setFormField,
	resetForm           : bindActionCreators(Actions, dispatch).resetForm,
	getPaymentMethods   : bindActionCreators(thunks, dispatch).getPaymentMethods,
	deletePaymentMethod : bindActionCreators(thunks, dispatch).deletePaymentMethod,
	setPreferredCard    : bindActionCreators(thunks, dispatch).setPreferredCard,
});

PaymentMethods.propTypes = {
	data                : PropTypes.object,
	isRkPay             : PropTypes.bool,
	loading             : PropTypes.bool,
	resetForm           : PropTypes.func.isRequired,
	setField            : PropTypes.func.isRequired,
	setRef              : PropTypes.func.isRequired,
	getPaymentMethods   : PropTypes.func.isRequired,
	deletePaymentMethod : PropTypes.func.isRequired,
	setPreferredCard    : PropTypes.func.isRequired,
	user                : PropTypes.object,
};

PaymentMethods.defaultProps = {
	data    : {},
	isRkPay : false,
	loading : false,
	user    : {},
};

export default connect(mapStateToProps, mapDispatchToProps)(PaymentMethods);
